// ─── Decision context helpers ──────────────────────────────────────────────────
//
// Pure, dependency-free resolution of a decision's agent-authored context —
// what it's about, why it's here, and what accepting vs rejecting does. Kept
// out of the React components so the fallback rules can be unit-tested.
//
// The API lifts this context onto `decision.context`. For resilience against
// legacy rows and raw SSE payloads (which carry only the opaque `detail` bag),
// we fall back to the reserved underscore-prefixed `detail` keys the backend
// writes. Either way the card gets a fully-populated, null-safe object.

import type { Decision, DecisionContext, DecisionWorkProduct } from './api'

const EMPTY: DecisionContext = {
  description: null,
  reason: null,
  acceptDescription: null,
  rejectDescription: null,
  acceptAction: null,
  workProduct: null,
}

const WORK_PRODUCT_KINDS = ['email', 'message', 'article', 'text', 'action'] as const

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null
}

function parseWorkProduct(v: unknown): DecisionWorkProduct | null {
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  const body = str(o.body)
  const kind = o.kind
  if (!body || typeof kind !== 'string') return null
  if (!(WORK_PRODUCT_KINDS as readonly string[]).includes(kind)) return null
  return { kind: kind as DecisionWorkProduct['kind'], heading: str(o.heading), body }
}

/**
 * Resolve a decision's agent-authored context. Prefers the first-class
 * `context` object from the API; falls back to reserved `detail` keys so SSE
 * and legacy rows still render sensibly.
 */
export function decisionContext(d: Decision): DecisionContext {
  if (d.context) return { ...EMPTY, ...d.context }

  const detail = d.detail
  if (!detail || typeof detail !== 'object') return EMPTY
  const o = detail as Record<string, unknown>
  return {
    description: str(o._description),
    reason: str(o._reason),
    acceptDescription: str(o._acceptDescription),
    rejectDescription: str(o._rejectDescription),
    acceptAction: str(o._acceptAction),
    workProduct: parseWorkProduct(o._workProduct),
  }
}

/**
 * Plain-English statement of what ACCEPT does. Uses the agent's own wording
 * when supplied; otherwise a safe, generic description so the button is never
 * a mystery.
 */
export function acceptConsequence(d: Decision, ctx: DecisionContext): string {
  if (ctx.acceptDescription) return ctx.acceptDescription
  return `${d.skillName} carries out this action now and it is recorded in the activity log.`
}

/** Plain-English statement of what REJECT does, with a generic fallback. */
export function rejectConsequence(d: Decision, ctx: DecisionContext): string {
  if (ctx.rejectDescription) return ctx.rejectDescription
  return `The action is cancelled and ${d.skillName} stands down — nothing is sent. Recorded in the activity log.`
}

/** Why this decision needs Mark, with a generic fallback keyed off its type. */
export function whyHere(d: Decision, ctx: DecisionContext, typeLabel: string): string {
  if (ctx.reason) return ctx.reason
  return `${typeLabel} actions need your sign-off before ${d.skillName} can carry them out.`
}
